import { treeHasCompleteCondition, type GroupNode } from './check-rule-model';
import type { CheckType } from '@internal/backstage-plugin-pulse-common';

// Check ids are snake_case, matching the config-authored checks.
const SNAKE_CASE_PATTERN = /^[a-z](?:[a-z0-9_]*[a-z0-9])?$/;

export type CheckFieldValues = {
  submitted: boolean;
  isEdit: boolean;
  id: string;
  name: string;
  description: string;
  ownerEntityRef: string;
  includeKinds: string[];
  excludeKinds: string[];
  ruleTree: GroupNode;
  passedMessage: string;
  failedMessage: string;
  /** Manual checks are decided by a user, so they carry no rule or messages. */
  type: CheckType;
};

/**
 * Derives per-field validation errors (only surfaced once `submitted`) plus an
 * overall `isValid` flag, so the form can highlight exactly which inputs still
 * need attention instead of showing a single generic message.
 */
export const computeCheckFieldErrors = (values: CheckFieldValues) => {
  const trimmedId = values.id.trim();
  const idFormatError =
    !values.isEdit &&
    trimmedId.length > 0 &&
    !SNAKE_CASE_PATTERN.test(trimmedId)
      ? 'Use snake_case: lowercase letters, numbers and underscores, starting and ending with a letter.'
      : undefined;
  const hasKind =
    values.includeKinds.some(Boolean) || values.excludeKinds.some(Boolean);
  const hasCompleteCondition =
    values.type === 'manual' || treeHasCompleteCondition(values.ruleTree);
  const hasPassedMessage =
    values.type === 'manual' || Boolean(values.passedMessage.trim());
  const hasFailedMessage =
    values.type === 'manual' || Boolean(values.failedMessage.trim());
  const flag = (invalid: boolean, message: string) =>
    values.submitted && invalid ? message : undefined;

  return {
    idFormatError,
    idFieldError: idFormatError ?? flag(!trimmedId, 'ID is required.'),
    nameError: flag(!values.name.trim(), 'Name is required.'),
    descriptionError: flag(
      !values.description.trim(),
      'Description is required.',
    ),
    ownerError: flag(!values.ownerEntityRef.trim(), 'Owner is required.'),
    kindsError: flag(
      !hasKind,
      'Select at least one kind to include or exclude.',
    ),
    conditionError: values.submitted && !hasCompleteCondition,
    passedMessageError: flag(!hasPassedMessage, 'Passed message is required.'),
    failedMessageError: flag(
      !hasFailedMessage,
      'Add a failure explanation and instructions for fixing the check.',
    ),
    isValid:
      !idFormatError &&
      Boolean(trimmedId) &&
      Boolean(values.name.trim()) &&
      Boolean(values.description.trim()) &&
      Boolean(values.ownerEntityRef.trim()) &&
      hasKind &&
      hasCompleteCondition &&
      hasPassedMessage &&
      hasFailedMessage,
  };
};
